import { NextRequest, NextResponse } from 'next/server'
import { and, eq, asc } from 'drizzle-orm'
import { requireExpertApiUser } from '@/lib/role-guard'
import { db } from '@/db'
import { projects, users } from '@/db/schema'

export async function GET() {
  const auth = await requireExpertApiUser()
  if (!auth.ok) return auth.response

  const rows = await db
    .select({
      id: projects.id,
      name: projects.name,
      isActive: projects.isActive,
      createdAt: projects.createdAt,
      createdByName: users.displayName,
      createdByEmail: users.email,
    })
    .from(projects)
    .leftJoin(users, eq(users.id, projects.createdById))
    .where(and(eq(projects.tenantId, auth.user.tenantId), eq(projects.isActive, true)))
    .orderBy(asc(projects.name))

  return NextResponse.json(rows)
}

function normaliseProjectName(raw: string): string {
  return raw.trim().replace(/^\w/, (c) => c.toUpperCase())
}

export async function POST(request: NextRequest) {
  const auth = await requireExpertApiUser()
  if (!auth.ok) return auth.response

  const body = await request.json().catch(() => null)
  if (!body?.name || typeof body.name !== 'string' || !body.name.trim()) {
    return NextResponse.json({ error: 'name is required' }, { status: 400 })
  }

  const name = normaliseProjectName(body.name)

  const [existing] = await db
    .select({ id: projects.id })
    .from(projects)
    .where(and(eq(projects.tenantId, auth.user.tenantId), eq(projects.name, name)))
    .limit(1)

  if (existing) {
    return NextResponse.json({ error: `Project "${name}" already exists` }, { status: 409 })
  }

  const [project] = await db
    .insert(projects)
    .values({ tenantId: auth.user.tenantId, name, createdById: auth.user.id })
    .returning()

  return NextResponse.json(project, { status: 201 })
}
